// ./src/components/features/Venues/VenueRequirementsEditor/VenueRequirementsEditor.tsx
//
// ROADMAP #170 (owner, 2026-10-20) — "Vendors must provide": what a VENUE requires of every vendor working there, or of
// one kind of vendor (a service category) — what document, what kind, by how many days before the event, and the words
// to the vendor. When an event at this venue gets a vendor, these are asked of it at once (the server applies them). Rows
// in order, saved WHOLE with the venue; the server refuses each problem at its row (`vendorRequirements[<i>].<field>`,
// read with `getIndexedFormErrors`). Shaped like the venue's `ServicePointsEditor` (the app has no generic array editor).
import React, { useCallback } from "react";
import { ActionIcon, Box, Button, Group, NumberInput, Paper, SimpleGrid, Stack, Text, TextInput, Textarea, Tooltip } from "@mantine/core";

import { VENDOR_DOCUMENT_TYPE, VENDOR_LIMITS } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";

import { VENDOR_DOCUMENT_TYPE_OPTIONS } from "../../EventVendors/VendorRequirementDrawer/VendorRequirementDrawer";
import { createEmptyVenueRequirement, VenueRequirementRow } from "./venueRequirementRows";

export * from "./venueRequirementRows";

export interface VenueRequirementsEditorProps {
  value: VenueRequirementRow[];
  onChange: (rows: VenueRequirementRow[]) => void;
  /** Row-scoped refusals from the server (`getIndexedFormErrors(error, "vendorRequirements").byIndex`). */
  errors?: Record<number, Record<string, string>>;
  /** The kinds of vendor (service categories) a requirement can be limited to. */
  categoryOptions: Array<{ value: string; label: string }>;
  disabled?: boolean;
}

/** A whole number of days from the input, or null (an empty box = no due date). */
const daysFrom = (v: string | number): number | null => {
  if (typeof v === "number") {
    return Number.isFinite(v) ? Math.trunc(v) : null;
  }

  const n = Number.parseInt(v, 10);

  return Number.isFinite(n) ? n : null;
};

export const VenueRequirementsEditor: React.FC<VenueRequirementsEditorProps> = ({ value, onChange, errors = {}, categoryOptions, disabled = false }) => {
  const patch = useCallback(
    (index: number, next: Partial<VenueRequirementRow>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, ...next } : row)));
    },
    [value, onChange]
  );

  const remove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const add = useCallback(() => {
    onChange([...value, createEmptyVenueRequirement(VENDOR_DOCUMENT_TYPE.INSURANCE)]);
  }, [value, onChange]);

  return (
    <Stack gap="sm">
      {value.length === 0 && (
        <Text size="md" c="dimmed">
          Nothing required yet. Add what every vendor working here must provide — a certificate of insurance naming the venue, a food permit for caterers.
        </Text>
      )}

      {value.map((row, index) => (
        <Paper key={row.key} withBorder radius="md" p="sm" data-venue-requirement-row={index}>
          <Group align="flex-start" wrap="nowrap" gap="xs">
            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <SelectField
                  size="md"
                  label="Document"
                  data={VENDOR_DOCUMENT_TYPE_OPTIONS}
                  value={row.documentType}
                  onChange={(_name, v) => patch(index, { documentType: v ?? VENDOR_DOCUMENT_TYPE.INSURANCE })}
                  allowDeselect={false}
                  error={errors[index]?.documentType}
                  disabled={disabled}
                />
                <TextInput
                  size="md"
                  label="What kind"
                  placeholder="e.g. General liability"
                  value={row.documentKind}
                  onChange={(e) => {
                    const documentKind = e.currentTarget.value;
                    patch(index, { documentKind });
                  }}
                  maxLength={VENDOR_LIMITS.KIND_MAX}
                  error={errors[index]?.documentKind}
                  disabled={disabled}
                />
                <SelectField
                  size="md"
                  label="From"
                  placeholder="Every vendor"
                  data={categoryOptions}
                  value={row.venueCategoryId}
                  onChange={(_name, v) => patch(index, { venueCategoryId: v ?? null })}
                  clearable
                  error={errors[index]?.venueCategoryId}
                  disabled={disabled}
                />
                <NumberInput
                  size="md"
                  label="Due, days before the event"
                  placeholder="No due date"
                  value={row.dueDaysBefore ?? ""}
                  onChange={(v) => patch(index, { dueDaysBefore: daysFrom(v) })}
                  min={0}
                  max={VENDOR_LIMITS.DUE_DAYS_BEFORE_MAX}
                  allowDecimal={false}
                  allowNegative={false}
                  error={errors[index]?.dueDaysBefore}
                  disabled={disabled}
                />
              </SimpleGrid>
              <Textarea
                size="md"
                label="Instructions to the vendor"
                placeholder={'e.g. "Name the venue as additional insured."'}
                value={row.instructions}
                onChange={(e) => {
                  const instructions = e.currentTarget.value;
                  patch(index, { instructions });
                }}
                autosize
                minRows={1}
                maxLength={VENDOR_LIMITS.INSTRUCTIONS_MAX}
                error={errors[index]?.instructions}
                disabled={disabled}
              />
            </Stack>
            <Tooltip label="Remove requirement" withArrow>
              <ActionIcon
                variant="subtle"
                color="red"
                size={44}
                mt={28}
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={`Remove requirement ${index + 1}`}
              >
                <AppIcon iconName="Trash" size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Paper>
      ))}

      <Box>
        <Button variant="light" size="md" onClick={add} disabled={disabled} leftSection={<AppIcon iconName="Plus" size={16} />}>
          Add requirement
        </Button>
      </Box>
    </Stack>
  );
};
